import { CatalogItem } from '../../domain/catalogItem'
import { hasValue } from '../../helpers/nullable'
import { applyCalculatorData, Budget, taxRateFractionToPercent, taxRatePercentToFraction } from '../Budgets/budgetModel'
import { CatalogItemFormValues, catalogItemToFormValues } from '../ItemCatalog/catalogItemModel'
import { ApplyBudgetData, BudgetReference, BudgetSummary, Worksheet } from './budgetCalculatorModel'
import { BudgetStatistics, ProfitData } from './budgetModel'

export type LaborSkuSettings = {
  projectLaborSKU: string
  maintenanceLaborSKU: string
}

const normalizeSku = (sku: string | null | undefined): string => (sku ?? '').trim().toUpperCase()

export type LaborItemRole = 'Project' | 'Maintenance'

export const laborItemRoles = (settings: LaborSkuSettings | undefined, item: CatalogItem): LaborItemRole[] => {
  const sku = normalizeSku(item.sku)
  if (!hasValue(settings) || sku.length === 0) {
    return []
  }

  const roles: LaborItemRole[] = []
  if (sku === normalizeSku(settings.projectLaborSKU)) {
    roles.push('Project')
  }
  if (sku === normalizeSku(settings.maintenanceLaborSKU)) {
    roles.push('Maintenance')
  }
  return roles
}

export const defaultLaborItemIds = (settings: LaborSkuSettings | undefined, items: CatalogItem[]): number[] => {
  return items.filter(item => laborItemRoles(settings, item).length > 0).map(item => item.id)
}

export const selectedLaborItems = (items: CatalogItem[], selectedIds: number[]): CatalogItem[] => {
  return items.filter(item => selectedIds.includes(item.id))
}

export const averageWageInCents = (summary: BudgetSummary): number => {
  return Math.round(summary.laborStatistics.manHourCost * 100)
}

export const laborItemPricing = (item: CatalogItem, costInCents: number): CatalogItemFormValues => ({
  ...catalogItemToFormValues(item),
  cost: costInCents,
  customPrice: null
})

export const laborItemPricingUpdates = (items: CatalogItem[], costInCents: number): CatalogItemFormValues[] => {
  return items.map(item => laborItemPricing(item, costInCents))
}

export const toApplyBudgetData = (
  budgetKey: string,
  statistics: BudgetStatistics,
  profit: ProfitData
): ApplyBudgetData => ({
  budgetKey,
  laborOverhead: statistics.laborMarkup,
  equipmentOverhead: profit.overheadRecoveryMarkup.equipment,
  materialsOverhead: profit.overheadRecoveryMarkup.material,
  subcontractorOverhead: profit.overheadRecoveryMarkup.subcontractor,
  profitMargin: statistics.netProfitMargin
})

export const applyYearOptions = (budgets: BudgetReference[]): number[] => {
  return budgets
    .map(budget => parseInt(budget.key))
    .filter(year => !isNaN(year))
    .sort((a, b) => b - a)
}

export const defaultApplyYear = (years: number[], currentYear: number): number | null => {
  if (years.length === 0) {
    return null
  }
  return years.includes(currentYear) ? currentYear : years[0]
}

export type LaborPricingAvailability = 'ready' | 'loading' | 'noLaborItems' | 'lookupFailed'

type LaborPricingLookup = {
  isLoading: boolean
  isError: boolean
  items: CatalogItem[]
}

export const laborPricingAvailability = (lookup: LaborPricingLookup): LaborPricingAvailability => {
  if (lookup.isLoading) {
    return 'loading'
  }
  if (lookup.isError) {
    return 'lookupFailed'
  }
  return lookup.items.length === 0 ? 'noLaborItems' : 'ready'
}

export const laborPricingUnavailableMessage = (availability: LaborPricingAvailability): string | null => {
  const messages: Record<LaborPricingAvailability, string | null> = {
    ready: null,
    loading: null,
    noLaborItems: 'Your catalog has no labor items, so labor pricing will be left alone.',
    lookupFailed: 'Your labor items could not be loaded, so labor pricing will be left alone.'
  }
  return messages[availability]
}

export const zeroWageWarning = (availability: LaborPricingAvailability, costInCents: number): string | null => {
  if (availability !== 'ready' || costInCents !== 0) {
    return null
  }
  return 'This worksheet has no employee hours, so its average wage is $0.00.'
}

export type BudgetSettings = {
  taxRate: number
  isDefault: boolean
}

export const budgetSettingsFrom = (budgets: Budget[] | undefined): BudgetSettings | null => {
  if (!hasValue(budgets) || budgets.length === 0) {
    return null
  }
  return {
    taxRate: budgets[0].taxRate,
    isDefault: budgets[0].isDefault
  }
}

export const withBudgetSettings = (budgets: Budget[], settings: BudgetSettings): Budget[] => {
  return budgets.map(budget => ({
    ...budget,
    taxRate: settings.taxRate,
    isDefault: settings.isDefault
  }))
}

export const taxRateInputValue = (settings: BudgetSettings | null): number | undefined => {
  return hasValue(settings) ? taxRatePercentToFraction(settings.taxRate) : undefined
}

export const withTaxRateInput = (settings: BudgetSettings, fraction: number | undefined): BudgetSettings => ({
  ...settings,
  taxRate: taxRateFractionToPercent(fraction ?? 0)
})

export type ApplyWorksheetRequest = {
  budgetWorksheetId: string
  year: number
  budgets: Budget[]
  laborItems: CatalogItemFormValues[]
  updateUnsoldJobs: boolean
}

type ApplyWorksheetInput = {
  worksheet: Worksheet
  statistics: BudgetStatistics
  year: number
  targetBudgets: Budget[]
  settings: BudgetSettings
  laborItems: CatalogItem[]
  selectedItemIds: number[]
  averageWageInCents: number
  updateUnsoldJobs: boolean
}

export const toApplyWorksheetRequest = (input: ApplyWorksheetInput): ApplyWorksheetRequest => {
  const budgetData = toApplyBudgetData(input.year.toString(), input.statistics, input.worksheet.data.profit)
  const selected = selectedLaborItems(input.laborItems, input.selectedItemIds)

  return {
    budgetWorksheetId: input.worksheet.budgetWorksheetId,
    year: input.year,
    budgets: withBudgetSettings(applyCalculatorData(input.targetBudgets, budgetData), input.settings),
    laborItems: laborItemPricingUpdates(selected, input.averageWageInCents),
    updateUnsoldJobs: input.updateUnsoldJobs
  }
}

export type ApplyWorksheetResult = {
  markedApplied: boolean
  laborItemsAttempted: number
  laborItemsPriced: number
  repricingFailed: boolean
}

export type ApplyNotification = {
  type: 'success' | 'warning'
  message: string
}

const laborPricingProblem = (result: ApplyWorksheetResult): string | null => {
  if (result.laborItemsPriced === result.laborItemsAttempted) {
    return null
  }
  if (result.laborItemsPriced === 0) {
    return 'your labor items could not be repriced'
  }
  return `only ${result.laborItemsPriced} of ${result.laborItemsAttempted} labor items could be repriced`
}

export const applyOutcomeNotification = (year: number, result: ApplyWorksheetResult): ApplyNotification => {
  const problems = [
    result.markedApplied ? null : 'the worksheet could not be marked as applied',
    laborPricingProblem(result),
    result.repricingFailed ? 'unsold jobs could not be repriced' : null
  ].filter(hasValue)

  if (problems.length === 0) {
    return { type: 'success', message: `This worksheet is now applied to your ${year} budget.` }
  }
  return { type: 'warning', message: `Saved your ${year} budget, but ${problems.join(', and ')}.` }
}

export const applyFailureMessage = 'This worksheet could not be applied. Please try again in a few minutes.'
